import { assertNever } from "@ondo/shared";

import { EmptyState } from "../lib/EmptyState.tsx";
import { LoadingScreen } from "../lib/Loading.tsx";
import { AccessTable } from "./AccessTable.tsx";
import { ActivityFilters } from "./ActivityFilters.tsx";
import { ActivityTable } from "./ActivityTable.tsx";
import { ActivityTabs } from "./ActivityTabs.tsx";
import {
  ACCESS_COPY,
  ACTIVITY_COPY,
  ACTIVITY_TABS,
  NOT_HERE,
} from "./activity.constants.ts";
import { useActivityController } from "./useActivityController.ts";
import type { AccessView, ActivityController, ActivityView } from "./useActivityController.ts";

import "./activity.css";

/**
 * Activity, and — since OND-68 — the access trail, as two tabs of one screen.
 *
 * TWO TABS AND NOT ONE MERGED LIST. The first reads Stream, which answers "what
 * happened" and is ALLOWED to lose a line, because `ActivityFeedService.record()`
 * swallows every error so an outage cannot fail a write. The second reads
 * `ondo.access_events`, which answers "who looked" and cannot lose a line:
 * append-only, undeletable by anybody including the service role, written inside
 * the request that served the thing. Interleaving them would make every row as
 * trustworthy as the weakest, and the audit's whole value is that it is not.
 *
 * THE TAB STRIP IS OUTSIDE BOTH PANELS' LOADING AND ERROR BRANCHES. A reader
 * whose feed has failed can still open the trail, and the other way round —
 * they come from different systems, so one being down is not this screen being
 * down.
 */
export function Activity({
  workBase,
  viewerOndoId,
  administers = false,
}: {
  workBase: string;
  viewerOndoId: string | null;
  /**
   * Whether this reader administers the open firm.
   *
   * Only ever changes which EMPTY SENTENCE the trail shows; the rows themselves
   * are decided by `access_events_read`. Default false, so a portal that has not
   * thought about it says the cautious thing — "you have opened nothing" —
   * rather than the false one, "nobody has".
   */
  administers?: boolean;
}) {
  const { tab, chooseTab, view, access, chooseUser, chooseAction }: ActivityController =
    useActivityController({ workBase, viewerOndoId, administers });

  const lead = ACTIVITY_TABS.find((entry) => entry.id === tab)?.lead ?? "";

  return (
    <div className="activity">
      <ActivityTabs active={tab} onSelect={chooseTab} />
      <p className="activity__lead">{lead}</p>

      {tab === "access" ? (
        <AccessPanel view={access} />
      ) : (
        <ActivityPanel view={view} onUser={chooseUser} onAction={chooseAction} />
      )}
    </div>
  );
}

/** The feed: what was DONE to the records this reader can see. */
function ActivityPanel({
  view,
  onUser,
  onAction,
}: {
  view: ActivityView;
  onUser: (id: string) => void;
  onAction: (id: string) => void;
}) {
  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={ACTIVITY_COPY.loading} />;

    case "error":
      return (
        <p role="alert" className="activity__error">
          {view.message}
        </p>
      );

    case "ready":
      return (
        <div className="activity__panel">
          <div className="activity__banner">
            <p>{ACTIVITY_COPY.banner}</p>
            <p className="activity__aside">{ACTIVITY_COPY.notInbox}</p>
          </div>

          <ActivityFilters
            users={view.users}
            actions={view.actions}
            onUser={onUser}
            onAction={onAction}
          />

          <div className="activity__caption">
            <p>{view.caption}</p>
            <p>{view.order}</p>
          </div>

          {view.notice ? (
            <p role="status" className="activity__notice">
              {view.notice}
            </p>
          ) : null}

          {view.empty ? (
            <EmptyState
              className="activity__empty"
              lead={view.empty.lead}
              detail={view.empty.detail}
            />
          ) : (
            <ActivityTable rows={view.rows} label={view.caption} />
          )}

          <div className="activity__absent">
            <h2 className="activity__absent-title">{NOT_HERE.title}</h2>
            <ul>
              {NOT_HERE.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        </div>
      );

    default:
      return assertNever(view);
  }
}

/**
 * The trail: what was SEEN.
 *
 * ITS BANNER'S SECOND SENTENCE IS THE ONE THAT HAD TO BE HERE. This screen's
 * whole history is a claim that outran what the product could do — "any time
 * anybody even accesses this or looks at it, all of that is recorded" — and the
 * recording half of that is now true. The half that will never be true is what
 * happened to what was seen, so the panel says what a row does NOT prove,
 * beside the rows, in the same breath. That is the same refusal OND-67 wrote
 * against `delivered_at`.
 *
 * NO FILTER CHIPS, unlike the feed. A trail is read to answer "who has been in
 * here", which is the unfiltered list, and a chip that quietly narrowed an audit
 * log would be the one control on this screen capable of hiding a line.
 */
function AccessPanel({ view }: { view: AccessView }) {
  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={ACCESS_COPY.loading} />;

    case "error":
      return (
        <p role="alert" className="activity__error">
          {view.message}
        </p>
      );

    case "ready":
      return (
        <div className="activity__panel">
          <div className="activity__banner">
            <p>{ACCESS_COPY.banner}</p>
            <p className="activity__aside">{ACCESS_COPY.limit}</p>
          </div>

          <div className="activity__caption">
            <p>{view.caption}</p>
            <p>{view.order}</p>
          </div>

          {view.notice ? (
            <p role="status" className="activity__notice">
              {view.notice}
            </p>
          ) : null}

          {view.empty ? (
            <EmptyState
              className="activity__empty"
              lead={view.empty.lead}
              detail={view.empty.detail}
            />
          ) : (
            <AccessTable rows={view.rows} label={view.caption} />
          )}
        </div>
      );

    default:
      return assertNever(view);
  }
}
